/**
 * Affiliate Config Tab
 *
 * Flat FormState pattern (mirrors ReferralSettingsTab): load program config,
 * edit default commission rate (shown as %, stored 0..1) + hold days,
 * Save (PUT) / Reset (re-fetch).
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Save, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { toast } from 'sonner';
import { useI18n } from '@/i18n';
import { affiliateApi, type AffiliateConfig } from '@/lib/api/affiliate';

const MIN_HOLD_DAYS = 0;
const MAX_HOLD_DAYS = 365;

interface FormState {
  // Percentage 0..100 for the UI; converted to 0..1 on save.
  defaultRatePercent: number;
  commissionHoldDays: number;
}

function formStateFromConfig(data: AffiliateConfig): FormState {
  return {
    defaultRatePercent: Math.round(data.default_commission_rate * 1000) / 10,
    commissionHoldDays: data.commission_hold_days,
  };
}

function rateOutOfRange(percent: number): boolean {
  return !Number.isFinite(percent) || percent < 0 || percent > 100;
}

function holdDaysOutOfRange(days: number): boolean {
  return !Number.isInteger(days) || days < MIN_HOLD_DAYS || days > MAX_HOLD_DAYS;
}

export function AffiliateConfigTab() {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['affiliate-config'],
    queryFn: affiliateApi.getConfig,
  });

  const [formState, setFormState] = useState<FormState | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  if (data && !formState) {
    setFormState(formStateFromConfig(data));
  }

  const mutation = useMutation({
    mutationFn: affiliateApi.updateConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['affiliate-config'] });
      toast.success(t('affiliatePage.configSaved'));
      setHasChanges(false);
    },
    onError: (err: Error) => {
      toast.error(`${t('affiliatePage.saveFailed')}: ${err.message}`);
    },
  });

  const handleChange = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    if (!formState) return;
    setFormState({ ...formState, [key]: value });
    setHasChanges(true);
  };

  const rateInvalid = formState != null && rateOutOfRange(formState.defaultRatePercent);
  const holdDaysInvalid = formState != null && holdDaysOutOfRange(formState.commissionHoldDays);
  const hasInvalidField = rateInvalid || holdDaysInvalid;

  const handleSave = () => {
    if (!formState || hasInvalidField) return;
    mutation.mutate({
      default_commission_rate: Math.round(formState.defaultRatePercent * 10) / 1000,
      commission_hold_days: formState.commissionHoldDays,
    });
  };

  const handleReset = () => {
    if (!data) return;
    setFormState(formStateFromConfig(data));
    setHasChanges(false);
  };

  return (
    <div className="max-w-2xl">
      <div className="flex justify-end gap-2 mb-6">
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          {t('affiliatePage.refresh')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!hasChanges || hasInvalidField || mutation.isPending}
        >
          <Save className="w-4 h-4 mr-2" />
          {mutation.isPending ? t('affiliatePage.saving') : t('affiliatePage.save')}
        </Button>
      </div>

      {error && (
        <Card className="mb-6 border-destructive">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0" />
            <div>
              <h3 className="text-destructive font-semibold">{t('affiliatePage.errorLoading')}</h3>
              <p className="text-destructive text-sm">{(error as Error).message}</p>
            </div>
          </div>
        </Card>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      )}

      {formState && (
        <Card>
          <h2 className="text-lg font-semibold text-foreground mb-1">
            {t('affiliatePage.configTitle')}
          </h2>
          <p className="text-sm text-muted-foreground mb-6">{t('affiliatePage.configDesc')}</p>

          <div className="space-y-6">
            <Input
              type="number"
              step="0.1"
              min={0}
              max={100}
              label={t('affiliatePage.defaultRateLabel')}
              value={formState.defaultRatePercent}
              onChange={(e) => handleChange('defaultRatePercent', parseFloat(e.target.value) || 0)}
              error={rateInvalid ? t('affiliatePage.rateRangeError') : undefined}
              helperText={t('affiliatePage.defaultRateHelp')}
            />
            <Input
              type="number"
              min={MIN_HOLD_DAYS}
              max={MAX_HOLD_DAYS}
              label={t('affiliatePage.holdDaysLabel')}
              value={formState.commissionHoldDays}
              onChange={(e) => handleChange('commissionHoldDays', parseInt(e.target.value, 10) || 0)}
              error={holdDaysInvalid ? t('affiliatePage.holdDaysRangeError') : undefined}
              helperText={t('affiliatePage.holdDaysHelp')}
            />
          </div>

          {hasChanges && (
            <div className="flex justify-end mt-6">
              <Button variant="outline" onClick={handleReset}>
                {t('affiliatePage.reset')}
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
